import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, ExternalLink, KeyRound, LogOut } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { create } from "zustand";
import { GithubMark } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/ui/controls";
import { Dialog, DialogContent, MenuItem, MenuSeparator } from "@/components/ui/overlay";
import { Input, Spinner } from "@/components/ui/primitives";
import { get } from "@/lib/api";
import { desktop } from "./bridge";

/**
 * Conta do GitHub do app (clonar, commit, push e pull). O token fica no cofre do sistema e nunca
 * chega à página; aqui só aparecem o login, o nome e a foto.
 */
export const githubAccountKey = ["git", "account"] as const;

export interface GitHubAccountInfo {
  connected: boolean;
  login?: string;
  name?: string;
  avatarUrl?: string;
}

export function useGitHubAccount(enabled = true) {
  return useQuery<GitHubAccountInfo>({
    queryKey: githubAccountKey,
    queryFn: () => desktop!.git.status(),
    enabled: enabled && Boolean(desktop),
    staleTime: 5 * 60_000,
  });
}

/** Diálogo "Conectar ao GitHub" (aberto do menu da conta ou do painel Git). */
export const useGitHubConnect = create<{ open: boolean; set(open: boolean): void }>((set) => ({
  open: false,
  set: (open) => set({ open }),
}));

export const openGitHubConnect = () => useGitHubConnect.getState().set(true);

const TOKEN_URL = "https://github.com/settings/tokens/new?scopes=repo,workflow&description=Kompil%20Desktop";

/** Itens do GitHub no menu da conta: conectar, ou a conta conectada (nome e @login) e desconectar. */
export function GitHubMenuItems() {
  const account = useGitHubAccount();
  const qc = useQueryClient();
  if (!desktop) return null;
  const data = account.data;
  if (!data?.connected) {
    return (
      <>
        <MenuSeparator />
        <MenuItem icon={<GithubMark className="h-3.5 w-3.5" />} onSelect={openGitHubConnect}>
          {account.isLoading ? "GitHub…" : "Conectar ao GitHub"}
        </MenuItem>
      </>
    );
  }
  return (
    <>
      <MenuSeparator />
      <div className="flex items-center gap-2.5 px-2 py-1.5" aria-label="Conta do GitHub conectada">
        {data.avatarUrl ? (
          <Avatar name={data.name ?? data.login ?? "GitHub"} src={data.avatarUrl} size={22} />
        ) : (
          <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-bg-emphasis">
            <GithubMark className="h-3 w-3" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-[12.5px] font-medium text-fg">{data.name ?? data.login ?? "GitHub"}</p>
          <p className="truncate text-[11px] text-fg-subtle">
            {data.login ? `@${data.login} · ` : ""}GitHub conectado
          </p>
        </div>
      </div>
      <MenuItem
        icon={<LogOut className="h-3.5 w-3.5" />}
        onSelect={() =>
          void desktop!.git.disconnect().then(() => {
            toast("GitHub desconectado deste computador.");
            void qc.invalidateQueries({ queryKey: githubAccountKey });
          })
        }
      >
        Desconectar do GitHub
      </MenuItem>
    </>
  );
}

type Step =
  | { kind: "starting" }
  | { kind: "code"; userCode: string; verificationUri: string }
  | { kind: "error"; message: string }
  | { kind: "token"; unavailable?: boolean };

/** Conexão pelo navegador (código de dispositivo), com o token pessoal como alternativa. */
export function GitHubConnectDialog() {
  const open = useGitHubConnect((s) => s.open);
  const setOpen = useGitHubConnect((s) => s.set);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {open ? (
        <DialogContent
          title="Conectar ao GitHub"
          description="Para clonar repositórios e enviar (push) direto do Kompil."
          className="max-w-[440px]"
        >
          <ConnectFlow onDone={() => setOpen(false)} />
        </DialogContent>
      ) : null}
    </Dialog>
  );
}

function ConnectFlow({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>({ kind: "starting" });
  const [copied, setCopied] = useState(false);
  const alive = useRef(true);
  const canDevice = Boolean(desktop?.git.deviceStart && desktop.git.deviceWait);

  const finish = (res: { login?: string; name?: string }) => {
    void qc.invalidateQueries({ queryKey: githubAccountKey });
    toast.success(`GitHub conectado como ${res.name ?? `@${res.login ?? "você"}`}.`);
    onDone();
  };

  const start = async () => {
    const git = desktop?.git;
    if (!git?.deviceStart || !git.deviceWait) return setStep({ kind: "token", unavailable: true });
    setStep({ kind: "starting" });
    setCopied(false);
    const providers = await get<{ githubClientId?: string | null }>("/auth/providers").catch(() => null);
    if (!providers?.githubClientId) {
      // Servidor sem o OAuth App do GitHub (GITHUB_CLIENT_ID): só o token pessoal.
      return alive.current && setStep({ kind: "token", unavailable: true });
    }
    const res = await git.deviceStart(providers.githubClientId);
    if (!alive.current) return;
    if (!res.ok || !res.userCode) {
      return setStep({ kind: "error", message: res.error ?? "Não foi possível falar com o GitHub." });
    }
    setStep({
      kind: "code",
      userCode: res.userCode,
      verificationUri: res.verificationUri ?? "https://github.com/login/device",
    });
    const done = await git.deviceWait();
    if (!alive.current) return;
    if (done.ok) finish(done);
    else if (done.error !== "Conexão cancelada.")
      setStep({ kind: "error", message: done.error ?? "Não foi possível conectar." });
  };

  useEffect(() => {
    alive.current = true;
    // Fora do efeito: o pedido do código começa logo depois de o diálogo montar.
    const timer = setTimeout(() => void start(), 0);
    return () => {
      clearTimeout(timer);
      alive.current = false;
      void desktop?.git.deviceCancel?.();
    };
    // Só ao abrir o diálogo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (step.kind === "token") {
    return (
      <div className="space-y-3 text-[12.5px] leading-relaxed text-fg-muted">
        {step.unavailable ? (
          <p className="rounded-md border border-border bg-bg px-3 py-2 text-[12px] text-fg-subtle">
            A conexão pelo navegador (com código) ainda não está ativa neste servidor. Por enquanto, use um
            token.
          </p>
        ) : null}
        <p>
          Crie um token com acesso a repositórios e cole aqui. Ele fica criptografado no cofre do sistema e só
          sai deste computador para o GitHub.
        </p>
        <TokenForm onDone={finish} />
        {canDevice && !step.unavailable ? (
          <button
            type="button"
            className="text-[12px] text-fg-subtle underline-offset-4 hover:text-fg hover:underline"
            onClick={() => void start()}
          >
            Voltar para a conexão pelo navegador
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-4 text-[12.5px] leading-relaxed text-fg-muted">
      {step.kind === "starting" ? (
        <div className="flex items-center gap-2 py-6 text-fg-muted">
          <Spinner /> Pedindo um código ao GitHub…
        </div>
      ) : step.kind === "error" ? (
        <div className="space-y-3">
          <p className="text-[var(--danger)]">{step.message}</p>
          <Button variant="secondary" onClick={() => void start()}>
            Tentar de novo
          </Button>
        </div>
      ) : (
        <>
          <ol className="list-decimal space-y-1 pl-5">
            <li>Copie o código abaixo.</li>
            <li>No GitHub, cole o código e autorize o Kompil.</li>
          </ol>
          <div className="flex items-center justify-center rounded-lg border border-border bg-bg py-4">
            <span className="select-all font-mono text-[26px] font-semibold tracking-[0.2em] text-fg">
              {step.userCode}
            </span>
          </div>
          <Button
            variant="primary"
            className="w-full"
            onClick={() => {
              desktop?.clipboard?.write(step.userCode);
              setCopied(true);
              desktop?.openExternal(step.verificationUri);
            }}
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Código copiado — abrimos o GitHub" : "Copiar código e abrir o GitHub"}
            <ExternalLink className="h-3 w-3 opacity-60" />
          </Button>
          <p className="flex items-center gap-2 text-[12px] text-fg-subtle">
            <Spinner /> Esperando a autorização no navegador…
          </p>
        </>
      )}
      <button
        type="button"
        className="text-[12px] text-fg-subtle underline-offset-4 hover:text-fg hover:underline"
        onClick={() => {
          void desktop?.git.deviceCancel?.();
          setStep({ kind: "token" });
        }}
      >
        Prefiro colar um token de acesso pessoal
      </button>
    </div>
  );
}

/** Token pessoal do GitHub (ghp_… ou github_pat_…), guardado no cofre do sistema. */
export function TokenForm({ onDone }: { onDone?: (res: { login?: string; name?: string }) => void }) {
  const qc = useQueryClient();
  const [token, setToken] = useState("");
  const connect = useMutation({
    mutationFn: () => desktop!.git.connect(token),
    onSuccess: (res) => {
      if (!res.ok) return toast.error(res.error ?? "Não foi possível conectar.");
      setToken("");
      if (onDone) return onDone(res);
      toast.success(`GitHub conectado como ${res.name ?? `@${res.login}`}.`);
      void qc.invalidateQueries({ queryKey: githubAccountKey });
    },
  });
  return (
    <>
      <Button variant="secondary" className="w-full" onClick={() => desktop?.openExternal(TOKEN_URL)}>
        <GithubMark className="h-3.5 w-3.5" /> Criar token no GitHub{" "}
        <ExternalLink className="h-3 w-3 opacity-60" />
      </Button>
      <form
        className="space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          connect.mutate();
        }}
      >
        <Input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="ghp_… ou github_pat_…"
          aria-label="Token do GitHub"
          autoComplete="off"
          spellCheck={false}
        />
        <Button
          type="submit"
          variant="primary"
          className="w-full"
          loading={connect.isPending}
          disabled={!token.trim()}
        >
          <KeyRound className="h-3.5 w-3.5" /> Conectar
        </Button>
      </form>
    </>
  );
}
